import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useLocation } from '@reach/router';
import cn from 'classnames';
import Modal from 'components/UI/Modal';
import Slider from 'components/UI/Slider';
import Icon from 'components/UI/Icon';
import FullScreenPlayerCard from './FullScreenPlayerCard';
import { observer, expertTalks as store } from 'store';
import { useClickOutside } from 'utils/hooks';
import { pushCustomEvent } from 'utils/analytics';
import { debounce } from 'utils';

import * as styles from './ExpertTalksModal.module.scss';

function ExpertTalksModal({ animation = 'opacity', isOpen, closeModalCallback, items }) {
  const location = useLocation();
  const modalRef = useRef();
  const sliderRef = useRef(null);

  const [current, setCurrent] = useState(store.currentIndex ?? 0);
  const [globalPlayback, setGlobalPlayback] = useState(false);

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const pushEvent = useCallback(() => {
    pushCustomEvent('ndrsn_insights_shorts', {
      action: 'slide_change',
      label: location.href,
    });
  }, [location.href]);

  const handleAfterChangeInternal = useCallback(
    index => {
      store.resetShortsStore();
      store.setCurrentIndex(index);
      if (globalPlayback) {
        store.setPlaying(true);
      }
      pushEvent();
    },
    [globalPlayback, pushEvent],
  );

  const handleAfterChange = useMemo(
    () => debounce(handleAfterChangeInternal, 100),
    [handleAfterChangeInternal],
  );

  const settings = {
    vertical: true,
    verticalSwiping: true,
    infinite: false,
    speed: 700,
    initialSlide: store.currentIndex ?? 0,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    arrows: false,
  };

  const handleNext = useCallback(() => {
    sliderRef.current?.slickNext();
  }, []);

  const handlePrev = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, []);

  useClickOutside([modalRef], closeModalCallback);
  const canScrollBack = current > 0;
  const canScrollForward = current < items.length - 1;
  return (
    <Modal
      isOpen={isOpen}
      closeModalCallback={closeModalCallback}
      closeButton={false}
      theme="expertTalks"
      contentMod="noPadding"
      animation={animation}
    >
      {onClose => (
        <div className={cn(styles.container, 'defaultSliderDarkBg')} ref={modalRef}>
          <button className={styles.closeButton} onClick={closeModalCallback}>
            <Icon name="close" className={styles.closeIcon} />
          </button>
          <Slider
            {...settings}
            beforeChange={setCurrentKey}
            afterChange={handleAfterChange}
            ref={sliderRef}
          >
            {items.map((item, i) => (
              <FullScreenPlayerCard
                key={item.id}
                active={i === current}
                loadVideo={i === current || i === current - 1 || i === current + 1}
                handleCloseModal={closeModalCallback}
                handleNext={handleNext}
                globalPlayback={globalPlayback}
                setGlobalPlayback={setGlobalPlayback}
                fullscreen={isOpen}
                {...item}
              />
            ))}
          </Slider>
          {items?.length > 1 && (
            <div className={cn(styles.controls)}>
              <button
                className={styles.arrow}
                onClick={handlePrev}
                disabled={!canScrollBack}
              ></button>
              <button
                className={cn(styles.arrow, styles.next)}
                onClick={handleNext}
                disabled={!canScrollForward}
              ></button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default observer(ExpertTalksModal);
